import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  LayoutGrid,
  ChevronRight,
  AppWindow,
  KeyRound,
  FileText,
  UserCheck,
  CreditCard,
  FolderLock,
  CheckSquare,
  StickyNote,
  Wallet,
  BellRing,
} from 'lucide-react';
import { useApplications } from '../hooks/useApplications';

// Explicit exclusions requested by user:
// Remove SSH Key, Wi-Fi, Bookmark, Recovery Code
const EXCLUDED_SLUGS = new Set([
  'ssh-keys',
  'ssh_keys',
  'ssh-key',
  'ssh',
  'wifi',
  'wi-fi',
  'bookmarks',
  'bookmark',
  'recovery-codes',
  'recovery_codes',
  'recovery-code',
  'recovery',
]);

const isExcludedApp = (app) => {
  if (!app) return true;
  const slug = (app.slug || '').toLowerCase().trim();
  const name = (app.name || '').toLowerCase().trim();
  if (EXCLUDED_SLUGS.has(slug)) return true;
  if (
    name.includes('ssh') ||
    name === 'wi-fi' ||
    name === 'wifi' ||
    name.includes('bookmark') ||
    name.includes('recovery code')
  ) {
    return true;
  }
  return false;
};

// Clean, short 1-word iOS-style app titles that never clip
const IOS_APP_NAMES = {
  passwords: 'Passwords',
  notes: 'Notes',
  identities: 'Identities',
  cards: 'Cards',
  documents: 'Documents',
  reminders: 'Reminders',
  tasks: 'Tasks',
  notepad: 'Notepad',
  'expense-tracker': 'Expenses',
  expenses: 'Expenses',
};

// Authentic vibrant iOS app icon themes with Apple-style gradients & glows
export const IOS_APP_THEMES = {
  passwords: {
    gradient: 'linear-gradient(145deg, #3b82f6 0%, #1d4ed8 100%)',
    shadow: '0 5px 12px rgba(37, 99, 235, 0.35)',
    icon: KeyRound,
  },
  notes: {
    gradient: 'linear-gradient(145deg, #10b981 0%, #047857 100%)',
    shadow: '0 5px 12px rgba(16, 185, 129, 0.35)',
    icon: FileText,
  },
  identities: {
    gradient: 'linear-gradient(145deg, #8b5cf6 0%, #6d28d9 100%)',
    shadow: '0 5px 12px rgba(139, 92, 246, 0.35)',
    icon: UserCheck,
  },
  cards: {
    gradient: 'linear-gradient(145deg, #f59e0b 0%, #b45309 100%)',
    shadow: '0 5px 12px rgba(245, 158, 11, 0.35)',
    icon: CreditCard,
  },
  documents: {
    gradient: 'linear-gradient(145deg, #06b6d4 0%, #0e7490 100%)',
    shadow: '0 5px 12px rgba(6, 182, 212, 0.35)',
    icon: FolderLock,
  },
  reminders: {
    gradient: 'linear-gradient(145deg, #ec4899 0%, #be185d 100%)',
    shadow: '0 5px 12px rgba(236, 72, 153, 0.35)',
    icon: BellRing,
  },
  tasks: {
    gradient: 'linear-gradient(145deg, #0d9488 0%, #115e59 100%)',
    shadow: '0 5px 12px rgba(13, 148, 136, 0.35)',
    icon: CheckSquare,
  },
  notepad: {
    gradient: 'linear-gradient(145deg, #eab308 0%, #a16207 100%)',
    shadow: '0 5px 12px rgba(234, 179, 8, 0.35)',
    icon: StickyNote,
  },
  'expense-tracker': {
    gradient: 'linear-gradient(145deg, #059669 0%, #064e3b 100%)',
    shadow: '0 5px 12px rgba(5, 150, 105, 0.35)',
    icon: Wallet,
  },
  expenses: {
    gradient: 'linear-gradient(145deg, #059669 0%, #064e3b 100%)',
    shadow: '0 5px 12px rgba(5, 150, 105, 0.35)',
    icon: Wallet,
  },
};

export default function AppLauncherDropdown() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { applications, loading, hasPermission } = useApplications();

  // Close on outside click or Escape key
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleLaunch = (slug) => {
    setOpen(false);
    navigate(`/apps/${slug}`);
  };

  // Only show authorized, enabled applications AND filter out ssh, wifi, bookmark, recovery-codes
  const visibleApps = applications
    .filter((app) => app.enabled && hasPermission(app.slug, 'access'))
    .filter((app) => !isExcludedApp(app));

  return (
    <div className="app-launcher-container" ref={dropdownRef}>
      <button
        type="button"
        className={`app-launcher-btn ${open ? 'active' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Applications Launcher"
        title="Applications Launcher"
        aria-expanded={open}
      >
        <LayoutGrid size={18} />
      </button>

      {open && (
        <div
          className="app-launcher-dropdown ios-app-library-dropdown"
          role="dialog"
          aria-label="Web Applications"
        >
          <div className="app-launcher-header">
            <div className="app-launcher-title-wrap">
              <h3 className="app-launcher-title">Web Apps</h3>
              <span className="app-launcher-count-pill">{visibleApps.length}</span>
            </div>
            <Link
              to="/apps"
              className="app-launcher-view-all"
              onClick={() => setOpen(false)}
            >
              <span>All Apps</span>
              <ChevronRight size={13} />
            </Link>
          </div>

          <div className="app-launcher-grid">
            {loading ? (
              <div className="app-launcher-empty">Loading apps...</div>
            ) : visibleApps.length === 0 ? (
              <div className="app-launcher-empty">No applications available</div>
            ) : (
              visibleApps.map((app, index) => {
                const theme = IOS_APP_THEMES[app.slug] || {
                  gradient: 'linear-gradient(145deg, #475569 0%, #1e293b 100%)',
                  shadow: '0 5px 12px rgba(0, 0, 0, 0.25)',
                  icon: AppWindow,
                };
                const IconComponent = theme.icon || AppWindow;
                const displayName =
                  IOS_APP_NAMES[app.slug] ||
                  app.name.replace(/^(Secure|Vault)\s+/i, '');

                return (
                  <button
                    key={app.id || app.slug}
                    type="button"
                    className="app-launcher-item ios-app-launcher-item"
                    onClick={() => handleLaunch(app.slug)}
                    title={app.description || app.name}
                    style={{ '--i': index }}
                  >
                    <div
                      className="app-launcher-icon-box ios-app-icon-squircle"
                      style={{
                        background: theme.gradient,
                        boxShadow: theme.shadow,
                      }}
                    >
                      <IconComponent size={20} color="#ffffff" strokeWidth={2.2} />
                      <div className="ios-icon-gloss" />
                    </div>
                    <span className="app-launcher-name ios-app-label" title={app.name}>
                      {displayName}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
